import { filetypemime } from 'magic-bytes.js';
import * as mimeDb from 'mime-db';

export interface FileValidationResult {
  valid: boolean;
  detectedType?: string;
  expectedType?: string;
  error?: string;
}

function lookupMimeType(extension: string): string | null {
  const ext = extension.toLowerCase();

  // Search through mime-db to find the MIME type for this extension
  for (const [mimeType, data] of Object.entries(mimeDb)) {
    const mimeData = data as { extensions?: string[] };
    if (mimeData.extensions && mimeData.extensions.includes(ext)) {
      return mimeType;
    }
  }

  return null;
}

function readFileHeader(file: File, length = 4100): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const buffer = e.target?.result as ArrayBuffer;
      buffer ? resolve(new Uint8Array(buffer)) : reject(new Error('Failed to read file'));
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file.slice(0, length));
  });
}

function getFileExtension(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() || '';
}

function isPlainText(fileBytes: Uint8Array): boolean {
  for (let i = 0; i < Math.min(fileBytes.length, 100); i++) {
    const byte = fileBytes[i];
    // Allow printable ASCII (32-126) and whitespace (tab, newline, carriage return)
    if (byte >= 32 && byte <= 126) continue;
    if (byte === 9 || byte === 10 || byte === 13) continue;
    // Non-text bytes early in the file indicate it's not plain text
    if (i < 10 && byte !== 0) return false;
  }
  return true;
}

export async function validateFileTypeMatchesExtension(file: File): Promise<FileValidationResult> {
  return readFileHeader(file)
    .then((fileBytes) => {
      const detectedMimeTypes = filetypemime(fileBytes);
      const extension = getFileExtension(file.name);

      if (!extension) {
        return { valid: false, error: 'File has no extension' };
      }

      const expectedType = lookupMimeType(extension);
      if (!expectedType) {
        return { valid: false, error: `Unknown file extension: .${extension}` };
      }

      // Detect file type from magic numbers or plain text analysis
      let detectedType: string | null = null;
      let detectedTypeDisplay: string | null = null;

      if (detectedMimeTypes?.length) {
        detectedType = detectedMimeTypes[0];
      } else if (isPlainText(fileBytes)) {
        // Plain text files don't have magic numbers - check for PHP code
        const fileContentString = String.fromCharCode(
          ...Array.from(fileBytes.slice(0, Math.min(fileBytes.length, 100))).filter(
            (byte) => byte >= 32 && byte <= 126,
          ),
        );
        if (fileContentString.includes('<?php') || fileContentString.includes('<?=')) {
          detectedType = 'application/x-httpd-php';
          detectedTypeDisplay = 'application/x-httpd-php (PHP file)';
        } else {
          detectedType = 'text/plain';
        }
      }

      // If type couldn't be determined, reject files that should have magic numbers
      if (!detectedType) {
        const extensionsRequiringMagicNumbers = [
          'docx',
          'xlsx',
          'pptx',
          'pdf',
          'doc',
          'xls',
          'ppt',
        ];
        if (extensionsRequiringMagicNumbers.includes(extension)) {
          return {
            valid: false,
            expectedType,
            error: `File extension .${extension} indicates ${expectedType} file, but file content does not match this format.`,
          };
        }
        return {
          valid: false,
          error:
            'Could not determine file type from file content. Please ensure the file is not corrupted.',
        };
      }

      // Check if a MIME type is text-based using mime-db data
      const isTextBased = (mimeType: string): boolean => {
        if (mimeType.startsWith('text/')) {
          return true;
        }
        const mimeData = mimeDb[mimeType] as { charset?: string } | undefined;
        return !!mimeData?.charset;
      };

      // Check if detected type matches expected type
      const isMatch =
        detectedType === expectedType ||
        detectedMimeTypes?.includes(expectedType) ||
        (detectedType === 'text/plain' && isTextBased(expectedType));

      if (isMatch) {
        return { valid: true, detectedType, expectedType };
      }

      // Prepare display type for error message
      if (!detectedTypeDisplay) {
        detectedTypeDisplay = detectedType;
      }

      // Enhance ZIP file detection - Office documents (DOCX, XLSX, PPTX) are ZIP archives
      if (detectedType === 'application/zip') {
        const searchBytes = fileBytes.slice(0, Math.min(fileBytes.length, 2000));
        const fileBytesString = String.fromCharCode(
          ...Array.from(searchBytes).filter((byte) => byte >= 32 && byte <= 126),
        );

        if (fileBytesString.includes('[Content_Types].xml')) {
          const officeTypes = [
            { patterns: ['word/', 'document.xml', 'word/document'], label: 'Word document' },
            { patterns: ['xl/', 'worksheets/', 'xl/workbook'], label: 'Excel spreadsheet' },
            { patterns: ['ppt/', 'slides/', 'ppt/presentation'], label: 'PowerPoint presentation' },
          ];
          const matchedType = officeTypes.find((type) =>
            type.patterns.some((pattern) => fileBytesString.includes(pattern)),
          );
          detectedTypeDisplay = matchedType
            ? `application/zip (${matchedType.label})`
            : 'application/zip (Office document)';
        } else {
          detectedTypeDisplay = 'application/zip (ZIP archive)';
        }
      }

      return {
        valid: false,
        detectedType,
        expectedType,
        error: `File is ${detectedTypeDisplay} but has incorrect .${extension} extension.`,
      };
    })
    .catch((error) => {
      return {
        valid: false,
        error: error instanceof Error ? error.message : 'Error validating file type',
      };
    });
}
